import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { notificationsQueries } from '@shared/apis/notifications';
import { adaptNotification } from '@shared/components/notifications-bell/Notifications.utils';
import { useAxiosQuery } from '@shared/hooks/axios';
import { useLazyLoadItems } from '@shared/hooks/lazyLoad';
import { ResolvedNotificationHistoryItem } from '@shared/types/notifications';

// Fetches a page at a time from GET /user/notifications/history, only once `enabled`
// (the History tab is selected), and appends each page into the lazy-loaded list as the
// panel scrolls towards the bottom.
export const useNotificationHistory = (enabled: boolean) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [page, setPage] = useState(1);

  const lazyLoadItems = useLazyLoadItems<ResolvedNotificationHistoryItem>();

  const historyPayload = useMemo(() => ({ query: { limit: '20', page: page.toString() } }), [page]);

  const historyQuery = useAxiosQuery(historyPayload, notificationsQueries.history, {
    enabled,
    keepPreviousData: true,
    onSuccess: (response) => {
      const items = response.data
        .map((item) => {
          const adapted = adaptNotification(item);

          if (!adapted) {
            return null;
          }

          const resolved: ResolvedNotificationHistoryItem = {
            ...adapted,
            outcome: item.outcome,
            outcomeLabel: item.outcome_string,
          };

          return resolved;
        })
        .filter((item): item is ResolvedNotificationHistoryItem => item !== null);

      // React Query re-fires onSuccess for the current page whenever the query re-enables
      // (e.g. leaving and re-entering the History tab), which would otherwise append the
      // same page a second time - so pages after the first are deduped against what's held.
      const newItems =
        response.meta.pagination.current_page === 1
          ? items
          : items.filter((item) => !lazyLoadItems.items.some((existing) => existing.id === item.id));

      lazyLoadItems.addItems(response.meta.pagination, newItems);
    },
  });

  const onLoadMore = useCallback((nextPage: number) => setPage(nextPage), []);

  const onScroll = useMemo(() => {
    if (historyQuery.isLoading || lazyLoadItems.pagination.current_page === lazyLoadItems.pagination.total_pages) {
      return undefined;
    }

    return debounce(() => {
      if (!scrollRef.current) {
        return;
      }

      const isAtBottom =
        scrollRef.current.scrollHeight - scrollRef.current.scrollTop <= scrollRef.current.clientHeight + 100;

      if (isAtBottom) {
        onLoadMore(lazyLoadItems.pagination.current_page + 1);
      }
    }, 100);
  }, [historyQuery.isLoading, lazyLoadItems.pagination.current_page, lazyLoadItems.pagination.total_pages, onLoadMore]);

  useEffect(() => {
    return () => onScroll?.cancel();
  }, [onScroll]);

  const result = {
    isLoading: historyQuery.isLoading,
    items: lazyLoadItems.items,
    onLoadMore,
    onScroll,
    pagination: lazyLoadItems.pagination,
    scrollRef,
  };

  return result;
};
